/**
 * Colour swatches on product cards in collection grids.
 *
 * snippets/card-product-swatches.liquid puts the whole result of a swap on each
 * radio as data attributes, so this element only moves those values into the
 * card. It deliberately does not reuse VariantSelects from
 * component-product-form.js: that class keys its container lookup off the
 * form's data-format, and setting format to 'grid' on these cards would also
 * swap the add-to-cart markup that the collection design depends on.
 *
 * Every lookup is scoped to the closest [data-swatch-card], so one card's
 * selection can never rewrite a sibling card's price, image or link.
 */
class CardSwatches extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('[data-swatch-card]');
    if (!this.card) return;

    // Same hook the product page uses for its selected-colour text.
    this.valueLabel = this.querySelector('.color-selected-option');
    this.addEventListener('change', this.onChange.bind(this));
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList.contains('card-swatches__input')) return;

    const data = input.dataset;

    if (this.valueLabel) this.valueLabel.textContent = input.value;

    this.updateVariantId(data.variantId);
    this.updatePrice(data);
    this.updateImage(data.image);
    this.updateLinks(data.url);
    this.updateAddButton(data.available === 'true');
  }

  /**
   * Point the card's own form at the chosen variant.
   * @param {string} variantId
   */
  updateVariantId(variantId) {
    if (!variantId) return;

    const form = this.card.querySelector('form[data-product-form]');
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    if (!idInput) return;

    idInput.value = variantId;
    // Apps and the cart drawer listen for this; the value alone is not enough.
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {DOMStringMap} data radio dataset holding pre-formatted money
   */
  updatePrice(data) {
    const price = this.card.querySelector('[data-currentPrice]');
    if (price && data.price) price.textContent = data.price;

    const compare = this.card.querySelector('[data-comparePrice]');
    if (!compare) return;

    // A variant with no compare-at price must clear the strikethrough rather
    // than leave the previous colour's crossed-out price on screen.
    const onSale = data.onSale === 'true';
    compare.textContent = onSale ? data.comparePrice : '';
    compare.hidden = !onSale;
  }

  /**
   * @param {string} src variant image, already sized by Liquid
   */
  updateImage(src) {
    if (!src) return;

    const target =
      this.card.querySelector('[data-feauredImage]') ||
      this.card.querySelector('.card-img img, .collection-image-wrapper img');
    if (!target) return;

    // Layouts 1 and 2 hand their images to lazysizes via data-srcset. Leaving
    // it in place lets lazysizes put the old colour back on the next resize.
    target.removeAttribute('data-srcset');
    target.removeAttribute('data-src');
    target.srcset = src;
    target.src = src;
  }

  /**
   * @param {string} url product url carrying ?variant=
   */
  updateLinks(url) {
    if (!url) return;
    this.card.querySelectorAll('.product-link').forEach((link) => {
      link.href = url;
    });
  }

  /**
   * @param {boolean} available
   */
  updateAddButton(available) {
    const addBtn = this.card.querySelector('[name="add"]');
    if (!addBtn) return;

    addBtn.disabled = !available;

    const text = addBtn.querySelector('.add-text');
    if (!text) return;

    text.textContent = available
      ? this.dataset.addText
      : this.dataset.soldoutText;
  }
}

customElements.define('card-swatches', CardSwatches);
